// src/components/FeatureMockup.jsx
// 14 micro UI previews that sit at the top of each feature card.
// Each is ~110px tall, drawn in inline SVG/divs — no images, no emojis.
import React from 'react';

const G = '#00e5a0';
const R = '#ff4d6d';
const B = '#4cc9f0';
const Y = '#ffd166';
const M = '#55556a';

const wrap = {
  background: '#0a0a0f',
  border: '1px solid #1a1a26',
  borderRadius: 8,
  height: 112,
  padding: 12,
  marginBottom: 14,
  overflow: 'hidden',
  position: 'relative',
};
const stripe = {
  position: 'absolute',
  top: 0, left: 14, right: 14,
  height: 1,
  background: `linear-gradient(90deg, transparent, ${G}55, transparent)`,
  opacity: 0.65,
  pointerEvents: 'none',
};
const label = (c = M) => ({
  fontSize: 8,
  color: c,
  fontFamily: "'DM Mono',monospace",
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
});

const Frame = ({ children, accent }) => (
  <div style={{ ...wrap, borderColor: accent ? accent + '22' : '#1a1a26' }}>
    <div style={stripe} />
    {children}
  </div>
);

// 0 — Trade Journal
function TradeJournal() {
  const rows = [
    { p: 'EURUSD', s: 'L', rr: '1.8R', v: '+$240', c: G },
    { p: 'NQ',     s: 'S', rr: '2.4R', v: '+$310', c: G },
    { p: 'GOLD',   s: 'L', rr: '0.6R', v: '-$80',  c: R },
  ];
  return (
    <Frame>
      <div style={label()}>Trade Journal</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 8, alignItems: 'center', fontSize: 10, fontFamily: "'DM Mono',monospace" }}>
            <span style={{ color: '#f0f0f5' }}>{r.p}</span>
            <span style={{ fontSize: 8, padding: '1px 5px', background: (r.s === 'L' ? G : R) + '14', color: r.s === 'L' ? G : R, border: `1px solid ${(r.s === 'L' ? G : R)}33`, borderRadius: 3 }}>{r.s === 'L' ? 'LONG' : 'SHORT'}</span>
            <span style={{ color: M }}>{r.rr}</span>
            <span style={{ color: r.c, fontWeight: 700 }}>{r.v}</span>
          </div>
        ))}
      </div>
    </Frame>
  );
}

// 1 — Performance Dashboard
function Dashboard() {
  return (
    <Frame>
      <div style={label()}>Dashboard</div>
      <svg viewBox="0 0 200 36" style={{ width: '100%', height: 32, marginTop: 6 }}>
        <defs>
          <linearGradient id="dashGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={G} stopOpacity="0.35" />
            <stop offset="100%" stopColor={G} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0,28 L25,24 L50,22 L80,18 L110,14 L140,10 L170,6 L200,3" fill="none" stroke={G} strokeWidth="1.5" />
        <path d="M0,28 L25,24 L50,22 L80,18 L110,14 L140,10 L170,6 L200,3 L200,36 L0,36 Z" fill="url(#dashGrad)" />
      </svg>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <div style={{ flex: 1, background: '#0d0d14', border: '1px solid #1e1e2e', borderRadius: 4, padding: '4px 6px' }}>
          <div style={{ fontSize: 7, color: M, fontFamily: "'DM Mono',monospace" }}>WR</div>
          <div style={{ fontSize: 11, color: G, fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>68%</div>
        </div>
        <div style={{ flex: 1, background: '#0d0d14', border: '1px solid #1e1e2e', borderRadius: 4, padding: '4px 6px' }}>
          <div style={{ fontSize: 7, color: M, fontFamily: "'DM Mono',monospace" }}>ROI</div>
          <div style={{ fontSize: 11, color: G, fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>+24%</div>
        </div>
      </div>
    </Frame>
  );
}

// 2 — ODF Score
function ODFScoreMini() {
  const r = 22;
  const c = 2 * Math.PI * r;
  const off = c * (1 - 82 / 100);
  return (
    <Frame>
      <div style={label()}>ODF Score</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
        <div style={{ position: 'relative' }}>
          <svg width="62" height="62" viewBox="0 0 60 60">
            <circle cx="30" cy="30" r={r} fill="none" stroke="#1a1a26" strokeWidth="4" />
            <circle cx="30" cy="30" r={r} fill="none" stroke={G} strokeWidth="4" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" transform="rotate(-90 30 30)" />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            <span style={{ fontSize: 13, color: '#f0f0f5', fontWeight: 700, fontFamily: "'DM Mono',monospace", lineHeight: 1 }}>82</span>
            <span style={{ fontSize: 6, color: G, fontFamily: "'DM Mono',monospace", letterSpacing: '0.08em' }}>GRADE A</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
          {[['Profit', 80], ['Risk', 72], ['Disc', 88]].map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 28, fontSize: 8, color: M, fontFamily: "'DM Mono',monospace" }}>{s[0]}</span>
              <div style={{ flex: 1, height: 3, background: '#1a1a26', borderRadius: 2 }}>
                <div style={{ width: `${s[1]}%`, height: '100%', background: G, borderRadius: 2 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Frame>
  );
}

// 3 — Pattern Insights
function Pattern() {
  const items = [['EURUSD', 76, G], ['NQ', 68, G], ['GOLD', 52, Y], ['BTC', 38, R]];
  return (
    <Frame accent={B}>
      <div style={label(B)}>Pattern Insights</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 42, fontSize: 8, color: M, fontFamily: "'DM Mono',monospace" }}>{it[0]}</span>
            <div style={{ flex: 1, height: 5, background: '#1a1a26', borderRadius: 2 }}>
              <div style={{ width: `${it[1]}%`, height: '100%', background: it[2], borderRadius: 2 }} />
            </div>
            <span style={{ fontSize: 8, color: it[2], fontFamily: "'DM Mono',monospace", width: 22, textAlign: 'right' }}>{it[1]}%</span>
          </div>
        ))}
      </div>
    </Frame>
  );
}

// 4 — Performance Coach
function Coach() {
  return (
    <Frame accent={Y}>
      <div style={label(Y)}>Coach Report</div>
      <div style={{ marginTop: 8, padding: 8, background: '#0d0d14', border: `1px solid ${G}22`, borderRadius: 5, position: 'relative' }}>
        <div style={{ position: 'absolute', left: 0, top: 6, bottom: 6, width: 2, background: G, borderRadius: 1 }} />
        <div style={{ paddingLeft: 8 }}>
          <div style={{ height: 4, width: '92%', background: '#2a2a3e', borderRadius: 2, marginBottom: 4 }} />
          <div style={{ height: 4, width: '74%', background: '#2a2a3e', borderRadius: 2, marginBottom: 4 }} />
          <div style={{ height: 4, width: '58%', background: '#2a2a3e', borderRadius: 2 }} />
        </div>
      </div>
      <div style={{ marginTop: 6, fontSize: 8, color: M, fontFamily: "'DM Mono',monospace" }}>
        Edge: 1.84 PF · Watch Asian risk
      </div>
    </Frame>
  );
}

// 5 — Performance Lab
function Lab() {
  const items = [['Edge real?', true], ['Risk OK?', false], ['Discipline?', true], ['Sample size', true]];
  return (
    <Frame accent={Y}>
      <div style={label(Y)}>Diagnostic</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 9, fontFamily: "'DM Mono',monospace" }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: (it[1] ? G : R) + '18', border: `1px solid ${(it[1] ? G : R)}55`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="7" height="7" viewBox="0 0 12 12" fill="none" stroke={it[1] ? G : R} strokeWidth="2" strokeLinecap="round">
                {it[1] ? <path d="M2 7l3 3 5-6" /> : <><path d="M3 3l6 6" /><path d="M9 3l-6 6" /></>}
              </svg>
            </span>
            <span style={{ color: '#8888a8' }}>{it[0]}</span>
          </div>
        ))}
      </div>
    </Frame>
  );
}

// 6 — Risk Manager
function Risk() {
  return (
    <Frame>
      <div style={label()}>Position Size</div>
      <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 7, color: M, fontFamily: "'DM Mono',monospace" }}>RISK</div>
          <div style={{ fontSize: 14, color: '#f0f0f5', fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>1.0%</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 7, color: M, fontFamily: "'DM Mono',monospace" }}>LOTS</div>
          <div style={{ fontSize: 14, color: G, fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>0.42</div>
        </div>
      </div>
      <div style={{ marginTop: 10, marginBottom: 8 }}>
        <div style={{ height: 4, background: '#1a1a26', borderRadius: 2, position: 'relative' }}>
          <div style={{ width: '42%', height: '100%', background: `linear-gradient(90deg, ${G}, ${B})`, borderRadius: 2 }} />
          <div style={{ position: 'absolute', left: '42%', top: -3, width: 2, height: 10, background: G, boxShadow: `0 0 6px ${G}` }} />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 7, color: M, fontFamily: "'DM Mono',monospace" }}>
        <span>0%</span><span>MAX 2%</span>
      </div>
    </Frame>
  );
}

// 7 — Discipline Tracker
function Discipline() {
  const states = [G, G, R, G, G, Y, G];
  return (
    <Frame>
      <div style={label()}>Discipline</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 16, height: 16, borderRadius: '50%', background: states[i] + '22', border: `1px solid ${states[i]}55`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: states[i] }} />
            </div>
            <span style={{ fontSize: 7, color: M, fontFamily: "'DM Mono',monospace" }}>{d}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 8, color: M, fontFamily: "'DM Mono',monospace", textAlign: 'center' }}>
        Rule adherence 86%
      </div>
    </Frame>
  );
}

// 8 — Calendar
function CalendarMini() {
  const days = Array.from({ length: 21 }, (_, i) => {
    const r = (i * 13) % 17;
    if (r < 8) return G;
    if (r < 13) return R;
    return null;
  });
  return (
    <Frame>
      <div style={label()}>Calendar</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginTop: 8 }}>
        {days.map((c, i) => (
          <div key={i} style={{
            aspectRatio: '1/1',
            borderRadius: 2,
            background: c ? c + '1f' : '#0d0d14',
            border: `1px solid ${c ? c + '55' : '#1a1a26'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {c && <div style={{ width: 3, height: 3, borderRadius: '50%', background: c }} />}
          </div>
        ))}
      </div>
    </Frame>
  );
}

// 9 — Live Charts (candles)
function Charts() {
  const candles = [
    { h: 22, b: 12, up: true }, { h: 26, b: 18, up: true }, { h: 20, b: 10, up: false },
    { h: 28, b: 18, up: true }, { h: 22, b: 12, up: false }, { h: 32, b: 22, up: true },
    { h: 30, b: 18, up: true }, { h: 24, b: 12, up: false }, { h: 34, b: 22, up: true },
    { h: 38, b: 26, up: true },
  ];
  return (
    <Frame>
      <div style={label()}>Live Charts</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 64, marginTop: 10 }}>
        {candles.map((c, i) => (
          <div key={i} style={{ flex: 1, position: 'relative', height: '100%' }}>
            <div style={{ position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)', width: 1, height: c.h, background: c.up ? G : R, opacity: 0.55 }} />
            <div style={{ position: 'absolute', bottom: 4 + (c.h - c.b) / 2, left: '50%', transform: 'translateX(-50%)', width: 6, height: c.b, background: c.up ? G : R, borderRadius: 1 }} />
          </div>
        ))}
      </div>
    </Frame>
  );
}

// 10 — Economic Calendar
function Econ() {
  const events = [
    { c: 'USD', imp: R, t: 'NFP', tm: '13:30' },
    { c: 'EUR', imp: Y, t: 'CPI', tm: '09:00' },
    { c: 'GBP', imp: G, t: 'Retail Sales', tm: '07:00' },
  ];
  return (
    <Frame>
      <div style={label()}>Economic Calendar</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
        {events.map((e, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', background: '#0d0d14', border: '1px solid #1a1a26', borderRadius: 3 }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: e.imp }} />
            <span style={{ fontSize: 8, color: M, fontFamily: "'DM Mono',monospace", width: 26 }}>{e.c}</span>
            <span style={{ flex: 1, fontSize: 9, color: '#8888a8', fontFamily: "'DM Mono',monospace" }}>{e.t}</span>
            <span style={{ fontSize: 8, color: M, fontFamily: "'DM Mono',monospace" }}>{e.tm}</span>
          </div>
        ))}
      </div>
    </Frame>
  );
}

// 11 — CSV Import
function CSV() {
  return (
    <Frame>
      <div style={label()}>CSV Import</div>
      <div style={{ marginTop: 10, padding: 14, border: `1px dashed ${G}55`, borderRadius: 6, textAlign: 'center', background: G + '08' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={G} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto', display: 'block' }}>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <path d="M7 10l5-5 5 5" />
          <path d="M12 5v12" />
        </svg>
        <div style={{ fontSize: 8, color: G, fontFamily: "'DM Mono',monospace", marginTop: 5, letterSpacing: '0.08em' }}>MT4.csv · MT5.csv</div>
      </div>
    </Frame>
  );
}

// 12 — Shareable Trade Data
function Share() {
  return (
    <Frame>
      <div style={label()}>Shareable Link</div>
      <div style={{ marginTop: 8, padding: 8, background: '#0d0d14', border: '1px solid #1a1a26', borderRadius: 5 }}>
        <div style={{ fontSize: 8, color: M, fontFamily: "'DM Mono',monospace", marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={G} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.71" />
          </svg>
          odftrades.com/u/...
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 7, color: M, fontFamily: "'DM Mono',monospace" }}>ROI</div>
            <div style={{ fontSize: 12, color: G, fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>+24%</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 7, color: M, fontFamily: "'DM Mono',monospace" }}>WR</div>
            <div style={{ fontSize: 12, color: G, fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>68%</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 7, color: M, fontFamily: "'DM Mono',monospace" }}>PF</div>
            <div style={{ fontSize: 12, color: B, fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>1.84</div>
          </div>
        </div>
      </div>
    </Frame>
  );
}

// 13 — Multiple Accounts
function Accounts() {
  const accs = [
    { t: 'LIVE',   v: '+$3,420', c: G },
    { t: 'PROP',   v: '+$1,840', c: B },
    { t: 'PRAC',   v: '+$240',   c: M },
  ];
  return (
    <Frame>
      <div style={label()}>Multiple Accounts</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
        {accs.map((a, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 8px', background: '#0d0d14', border: `1px solid ${a.c}22`, borderRadius: 4 }}>
            <span style={{ fontSize: 9, color: a.c, fontFamily: "'DM Mono',monospace", letterSpacing: '0.06em' }}>{a.t}</span>
            <span style={{ fontSize: 11, color: '#f0f0f5', fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>{a.v}</span>
          </div>
        ))}
      </div>
    </Frame>
  );
}

const MAP = [
  TradeJournal, Dashboard, ODFScoreMini, Pattern, Coach, Lab, Risk, Discipline,
  CalendarMini, Charts, Econ, CSV, Share, Accounts,
];

export default function FeatureMockup({ index }) {
  const Component = MAP[index];
  return Component ? <Component /> : null;
}
